import { Box, useTheme } from "@mui/material";
import { ReactNode, useEffect, useId, useRef } from "react";
import "swiper/css";
import "swiper/css/pagination";
import { Keyboard, Mousewheel, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import SliderNavigation from "./sliderNavigation";
import SliderPagination from "./sliderPagination";

type Card = {
  id: string;
  content: ReactNode | ReactNode[];
};

type BreakpointConfig = {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
};

type Props = {
  primaryColor: string;
  cards: Card[];
  style?: React.CSSProperties;
  slidesPerBreakpoint?: BreakpointConfig;
};

export default function CardSlider({
  primaryColor,
  cards,
  style,
  slidesPerBreakpoint,
}: Props) {
  const swiperRef = useRef<SwiperRef>(null);

  // If we have multiple instances of the slider on the same page,
  // we need to generate a unique ID for each instance so the navigation and pagination
  // classes are not the same for all instances.
  const rawId = useId();
  const sliderId = rawId.replace(/:/g, "-"); // Replace colons with dashes to make it CSS-safe

  const isDesktop = useMoxieMediaQuery("desktop", "up");
  const theme = useTheme();

  const { xs, sm, md, lg } = theme.breakpoints.values;

  // This effect is needed as we conditionally show the navigation buttons
  useEffect(() => {
    const swiperInstance = swiperRef.current?.swiper;

    if (swiperInstance) {
      if (isDesktop) {
        swiperInstance.navigation.init();
        swiperInstance.navigation.update();
      } else {
        swiperInstance.navigation.destroy();
      }
    }
  }, [isDesktop]);

  return (
    <>
      <Box sx={{ position: "relative", p: 0 }}>
        <Swiper
          ref={swiperRef}
          modules={[Navigation, Pagination, Mousewheel, Keyboard]}
          spaceBetween={24}
          slidesPerView="auto"
          mousewheel={{
            forceToAxis: true,
          }}
          keyboard
          navigation={{
            nextEl: `.swiper-button-next-${sliderId}`,
            prevEl: `.swiper-button-prev-${sliderId}`,
          }}
          pagination={{
            clickable: true,
            el: `.custom-swiper-pagination-${sliderId}`,
          }}
          breakpoints={{
            [xs]: {
              slidesPerView: slidesPerBreakpoint?.xs ?? 1,
            },
            [sm]: {
              slidesPerView: slidesPerBreakpoint?.sm ?? 2,
            },
            [md]: {
              slidesPerView: slidesPerBreakpoint?.md ?? 3,
            },
            [lg]: {
              slidesPerView: slidesPerBreakpoint?.lg ?? 4,
            },
          }}
          style={{
            padding: "24px 0",
            ...style,
          }}
        >
          {cards.map((card) => (
            <SwiperSlide
              key={card.id}
              style={{
                height: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {card.content}
            </SwiperSlide>
          ))}
        </Swiper>
        {isDesktop && (
          <SliderNavigation primaryColor={primaryColor} sliderId={sliderId} />
        )}
      </Box>
      <SliderPagination primaryColor={primaryColor} sliderId={sliderId} />
    </>
  );
}
